<template>
  <v-dialog
    v-model="dialogOpen"
    max-width="500"
    persistent
  >
    <v-card class="custom-quantity-dialog">
      <v-card-title class="dialog-header primary white--text">
        <h2 class="text-h5">Prilagođena Količina</h2>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pb-0 pt-4">
        <!-- Display Field -->
        <v-text-field
          ref="displayField"
          v-model="displayValue"
          outlined
          readonly
          large
          class="quantity-display text-h4 text-center"
          placeholder="0"
          hide-details
          @click="selectDisplayValue"
          @focus="selectDisplayValue"
        ></v-text-field>

        <!-- Quick Buttons -->
        <v-row dense class="mb-3 mt-4">
          <v-col cols="4" v-for="val in quickValues" :key="val">
            <v-btn
              block
              large
              outlined
              color="primary"
              @click="setQuickValue(val)"
              class="quick-value-btn"
            >
              {{ val }}
            </v-btn>
          </v-col>
        </v-row>

        <!-- Numpad -->
        <v-row dense>
          <v-col cols="4" v-for="num in [7,8,9,4,5,6,1,2,3]" :key="num">
            <v-btn
              block
              x-large
              @click="appendDigit(num)"
              class="numpad-btn"
              color="grey lighten-2"
            >
              <span class="text-h5">{{ num }}</span>
            </v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn
              block
              x-large
              @click="appendDecimal"
              class="numpad-btn"
              color="grey lighten-2"
              :disabled="displayValue.includes('.')"
            >
              <span class="text-h5">.</span>
            </v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn
              block
              x-large
              @click="appendDigit(0)"
              class="numpad-btn"
              color="grey lighten-2"
            >
              <span class="text-h5">0</span>
            </v-btn>
          </v-col>
          <v-col cols="4">
            <v-btn
              block
              x-large
              @click="backspace"
              class="numpad-btn"
              color="grey lighten-2"
            >
              <v-icon large>mdi-backspace</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <!-- Validation Message -->
        <v-alert
          v-if="displayValue && !isValid"
          type="error"
          dense
          class="mt-3"
          text
        >
          Unesite vrijednost između 0.1 i 9999
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-4 pb-4 pt-2">
        <v-btn text @click="close">Odustani</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          large
          @click="confirm"
          :disabled="!isValid"
          class="confirm-btn"
        >
          Potvrdi
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'CustomQuantityDialog',

  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    currentQuantity: {
      type: Number,
      default: 1
    }
  },

  data () {
    return {
      dialogOpen: false,
      displayValue: '',
      quickValues: [0.5, 1.5, 2.5],
      firstDigitAfterOpen: false
    }
  },

  computed: {
    isValid () {
      if (!this.displayValue) {
        return false
      }
      const num = parseFloat(this.displayValue)
      return !isNaN(num) && num >= 0.1 && num <= 9999
    }
  },

  watch: {
    isOpen (val) {
      this.dialogOpen = val
      if (val) {
        // Initialize with current quantity
        this.displayValue = this.currentQuantity.toString()
        // Mark that the next digit should replace the current value
        this.firstDigitAfterOpen = true
      }
    }
  },

  methods: {
    appendDigit (digit) {
      // If this is the first digit after opening, replace the current value
      if (this.firstDigitAfterOpen) {
        this.displayValue = digit.toString()
        this.firstDigitAfterOpen = false
        return
      }
      // Limit input length to 6 characters
      if (this.displayValue.length < 6) {
        this.displayValue += digit.toString()
      }
    },

    appendDecimal () {
      // If this is the first input after opening, start with "0."
      if (this.firstDigitAfterOpen) {
        this.displayValue = '0.'
        this.firstDigitAfterOpen = false
        return
      }
      // Add decimal point if not already present
      if (!this.displayValue.includes('.')) {
        this.displayValue += this.displayValue ? '.' : '0.'
      }
    },

    backspace () {
      this.displayValue = this.displayValue.slice(0, -1)
    },

    setQuickValue (val) {
      this.displayValue = val.toString()
      // Automatically confirm and close when clicking quick value
      this.$emit('custom-quantity-selected', val)
      this.close()
    },

    selectDisplayValue () {
      // Select all text in the display field when clicked
      this.$nextTick(() => {
        if (this.$refs.displayField && this.$refs.displayField.$refs.input) {
          this.$refs.displayField.$refs.input.select()
        }
      })
      // Also mark that next digit should replace the value
      this.firstDigitAfterOpen = true
    },

    confirm () {
      if (this.isValid) {
        const quantity = parseFloat(this.displayValue)
        this.$emit('custom-quantity-selected', quantity)
        this.close()
      }
    },

    close () {
      this.dialogOpen = false
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.custom-quantity-dialog {
  border-radius: 12px;
}

.dialog-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.quantity-display >>> input {
  text-align: center;
  font-size: 2rem;
  font-weight: 600;
  color: #1976d2;
}

.quick-value-btn {
  min-height: 48px;
  font-weight: 600;
  font-size: 16px;
}

/* Touch-friendly numpad buttons */
.numpad-btn {
  min-height: 60px;
  font-weight: 600;
  margin-bottom: 4px;
}

.numpad-btn:hover {
  background-color: #e0e0e0 !important;
}

.confirm-btn {
  min-width: 120px;
  font-weight: 600;
}
</style>
